import React, { useEffect, useState } from 'react'
import { IContextMenuState } from 'types'
import { INITIAL_CONTEXT_MENU_STATE } from 'app-constants'
import { PERMISSIONS } from 'helpers/auth'
import {
  CollapseAll,
  Navigation,
  Options,
  ExpandAll,
  Pencil,
  Word,
  Audit,
  Permissions as PermissionsIcon,
  Tick
} from 'components/icons'
import Tooltip from 'components/tooltip'
import SettingsMenu from 'components/document/components/menu/views/settings'
import SearchMenu from 'components/document/components/menu/views/search'
import AuditModal from 'components/document/components/modal/audit'
import ExportModal from 'components/document/components/modal/export'
import PermissionsModal from 'components/document/components/modal/permissions'
import DefaultVariablesModal from 'components/document/components/modal/variables'
import { useDataContext } from 'components/document/contexts/data'
import { useApplicationStore } from 'hooks/application'
import RestoreSection from 'components/document/components/modal/restoreSection'
import Duplicate from 'components/duplicate'
import AuthModal from 'components/auth'
import Delete from 'components/delete'
import ArchiveExplanation from 'components/delete/ArchiveExplanation'
import useProject from 'hooks/useProject'
import api from 'helpers/api'
import DuplicateChildren from 'components/duplicateChildren'
import Button from 'components/button'
import { useAuth } from 'hooks/auth'
import { useSearchParams } from 'react-router-dom'
import PageHeader from 'components/pageHeader'
import { PAGE_HEADER_ACTIONS, PAGE_HEADER_GROUP, pageHeaderButton } from 'components/pageHeader/styles'

// The document's header: the shared page header, with the document's actions.
// Edit sits apart as the one labelled action; the rest are grouped by what
// they act on.
const { icon, action } = pageHeaderButton()

// Below 30rem the labelled Edit, three groups and their gaps don't fit beside the Show sidebar
// button, so Edit drops its word and the groups close up rather than any action being cut off.
const NARROW_HIDDEN_LABEL = '@max-[30rem]/document:sr-only'
const NARROW_ACTIONS = '@max-[30rem]/document:gap-1 @max-[30rem]/document:ml-0'

interface ToolbarProps {
  addSection: (parentId: string | null) => void
}

const Toolbar: React.FC<ToolbarProps> = ({ addSection }) => {
  const { project, projectAdmin } = useProject()
  const { user } = useAuth()
  const {
    process,
    updateProcess,
    editMode,
    setEditMode,
    navigationMenu,
    setNavigationMenu,
    expandAllSections,
    collapseAllSections
  } = useDataContext()
  const { setSnackbarMessage, displayErrorMessage } = useApplicationStore()
  const [searchParams] = useSearchParams()

  const copyDoc = searchParams.get('copyDoc')

  const [settingsMenu, setSettingsMenu] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)
  const [searchMenu, setSearchMenu] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)
  const [auditModal, setAuditModal] = useState<boolean>(false)
  const [exportModal, setExportModal] = useState<boolean>(false)
  const [restoreSectionModal, setRestoreSectionModal] = useState<boolean>(false)
  const [duplicateModal, setDuplicateModal] = useState<boolean>(false)

  useEffect(() => {
    if (user && copyDoc) {
      setDuplicateModal(true)
    }
  }, [copyDoc])
  const [authModal, setAuthModal] = useState<boolean>(false)
  const [archiveModal, setArchiveModal] = useState<boolean>(false)
  const [permissionsModal, setPermissionsModal] = useState<boolean>(false)
  const [defaultVariablesModal, setDefaultVariablesModal] = useState<boolean>(false)
  const [duplicateChildrenModal, setDuplicateChildrenModa] = useState<boolean>(false)
  const [archivingProcess, setArchivingProcess] = useState<{ isArchiving: boolean; status: string | null }>({
    isArchiving: false,
    status: null
  })
  const [allExpanded, setAllExpanded] = useState<boolean>(process && process.expandByDefault ? true : false)

  const settingsWidth = 400
  const searchWidth = 400

  const isAdmin = process && process.permissionLevel === PERMISSIONS.owner
  const canExport = project.allowDocumentExport || projectAdmin
  const canAdminister = isAdmin && process && !process.isDeleted

  if (archivingProcess.status) {
    setSnackbarMessage({
      status: archivingProcess.status
    })
    setArchivingProcess({ ...archivingProcess, status: null })
  }

  const archiveProcess = async (): Promise<void> => {
    if (!project || !process) return
    await api
      .deleteProcess(process.publicId, { context: { projectId: project.publicId } })
      .then(() => {
        updateProcess({ ...process, isDeleted: true, deletedAt: new Date().toISOString() })
      })
      .catch((e) => {
        displayErrorMessage(e)
        return Promise.reject(e)
      })
  }

  const selectedTemplate = () => {
    if (user) {
      setDuplicateModal(true)
    } else {
      setAuthModal(true)
    }
  }

  if (process) {
    return (
      <PageHeader
        name={process.name}
        resource={{ kind: 'document', publicId: process.publicId }}
        testIdPrefix="document"
      >
        {process.isTemplate && (
          <Button internalType="accept" onClick={() => selectedTemplate()} className={action()}>
            {project.public && !user ? 'Copy doc for free' : 'Copy doc'}
          </Button>
        )}

        {isAdmin && !process.isDeleted && (
          <Tooltip content={editMode ? 'Stop editing. Changes save automatically.' : 'Edit this document'}>
            <Button
              internalType="transparent"
              className={action({ pressed: editMode })}
              data-testid="edit-mode-toggle"
              data-tour="document-edit-toggle"
              aria-pressed={editMode}
              onClick={() => setEditMode(!editMode)}
            >
              {editMode ? <Tick /> : <Pencil />}
              {/* Just the icon on a narrow page, so every action keeps its room; the word stays
                  for screen readers. */}
              <span className={NARROW_HIDDEN_LABEL}>{editMode ? 'Done' : 'Edit'}</span>
            </Button>
          </Tooltip>
        )}

        <div className={`${PAGE_HEADER_ACTIONS} ${NARROW_ACTIONS}`}>
          {/* How you read the page. */}
          <div className={PAGE_HEADER_GROUP} role="group" aria-label="View">
            <Tooltip content={navigationMenu ? 'Hide navigation' : 'Show navigation'}>
              <Button
                internalType="transparent"
                className={icon({ active: navigationMenu })}
                data-testid="navigation-menu-toggle"
                aria-pressed={navigationMenu}
                aria-label={navigationMenu ? 'Hide navigation' : 'Show navigation'}
                onClick={() => setNavigationMenu(!navigationMenu)}
              >
                <Navigation />
              </Button>
            </Tooltip>

            <Tooltip content={`${allExpanded ? 'Collapse' : 'Expand'} all sections`}>
              <Button
                internalType="transparent"
                className={icon()}
                data-testid="expand-collapse-toggle"
                aria-label={`${allExpanded ? 'Collapse' : 'Expand'} all sections`}
                onClick={() => {
                  if (allExpanded) {
                    collapseAllSections()
                    setAllExpanded(false)
                  } else {
                    expandAllSections()
                    setAllExpanded(true)
                  }
                }}
              >
                {allExpanded ? <CollapseAll /> : <ExpandAll />}
              </Button>
            </Tooltip>
          </div>

          {/* Who has done and can do what: the document's history and access. */}
          {canAdminister && (
            <div className={PAGE_HEADER_GROUP} role="group" aria-label="History and access">
              {canAdminister && (
                <Tooltip content="Audit History">
                  <Button
                    internalType="transparent"
                    className={icon()}
                    data-testid="document-audit-button"
                    aria-label="Audit History"
                    onClick={() => setAuditModal(true)}
                  >
                    <Audit />
                  </Button>
                </Tooltip>
              )}

              {canAdminister && (
                <Tooltip content="Permissions">
                  <Button
                    internalType="transparent"
                    className={icon()}
                    data-testid="document-permissions-button"
                    aria-label="Permissions"
                    onClick={() => setPermissionsModal(true)}
                  >
                    <PermissionsIcon />
                  </Button>
                </Tooltip>
              )}
            </div>
          )}

          {/* Exporting, then the overflow, together at the end. */}
          {(canExport || isAdmin) && (
            <div className={PAGE_HEADER_GROUP} role="group" aria-label="Export and more">
              {canExport && (
                <Tooltip content="Export as Word document">
                  <Button
                    internalType="transparent"
                    className={icon()}
                    data-testid="export-document-button"
                    data-tour="document-export-button"
                    aria-label="Export as Word document"
                    onClick={() => {
                      expandAllSections()
                      setAllExpanded(true)
                      setExportModal(true)
                    }}
                  >
                    <Word />
                  </Button>
                </Tooltip>
              )}

              {isAdmin && (
                <Tooltip content="More actions">
                  <Button
                    internalType="transparent"
                    className={icon({ active: settingsMenu.open })}
                    data-testid="document-settings-button"
                    data-tour="document-settings-button"
                    aria-label="More actions"
                    aria-haspopup="menu"
                    aria-expanded={settingsMenu.open}
                    // The menu closes on any mousedown outside it, and this button
                    // counts as outside: the press would close it, then the click
                    // reopen it. While it's open, keep the press from reaching the
                    // menu, and let the click toggle it closed instead.
                    onMouseDown={(event: React.MouseEvent<HTMLButtonElement>) => {
                      if (settingsMenu.open) event.stopPropagation()
                    }}
                    onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                      if (settingsMenu.open) {
                        setSettingsMenu(INITIAL_CONTEXT_MENU_STATE)
                        return
                      }
                      setSettingsMenu({
                        open: true,
                        top: `${event.currentTarget.getBoundingClientRect().bottom + 6}px`,
                        left: `${event.currentTarget.getBoundingClientRect().right - settingsWidth}px`,
                        right: 'auto',
                        bottom: 'auto'
                      })
                    }}
                  >
                    <Options />
                  </Button>
                </Tooltip>
              )}
            </div>
          )}
        </div>

        {settingsMenu.open && (
          <SettingsMenu
            id={`settings-process-context-menu`}
            menuState={settingsMenu}
            setMenuState={setSettingsMenu}
            width={settingsWidth}
            setExportModal={(open: boolean) => {
              setExportModal(open)
              if (open) {
                setSettingsMenu(INITIAL_CONTEXT_MENU_STATE)
                expandAllSections()
                setAllExpanded(true)
              }
            }}
            addSection={addSection}
            setRestoreSectionModal={setRestoreSectionModal}
            setDuplicateModal={setDuplicateModal}
            setArchiveModal={setArchiveModal}
            setDefaultVariablesModal={setDefaultVariablesModal}
            setViewDuplicatesModal={setDuplicateChildrenModa}
          />
        )}

        {searchMenu.open && (
          <SearchMenu
            id={`search-process-context-menu`}
            menuState={searchMenu}
            setMenuState={setSearchMenu}
            width={searchWidth}
          />
        )}
        {auditModal && <AuditModal id="process-audit-modal" open={auditModal} setOpen={setAuditModal} />}
        {exportModal && (
          <ExportModal
            id="export-process-modal"
            open={exportModal}
            setOpen={setExportModal}
            onClose={() => {
              setSettingsMenu(INITIAL_CONTEXT_MENU_STATE)
            }}
          />
        )}
        {archiveModal && (
          <Delete
            id="archive-process-modal"
            resourceName={process.name}
            open={archiveModal}
            setOpen={setArchiveModal}
            deleteResource={archiveProcess}
            otherContent={<ArchiveExplanation kind="document" />}
          />
        )}
        {restoreSectionModal && (
          <RestoreSection
            id="restore-section-modal"
            open={restoreSectionModal}
            setOpen={setRestoreSectionModal}
            onClose={() => setSettingsMenu(INITIAL_CONTEXT_MENU_STATE)}
          />
        )}
        {duplicateModal && (
          <Duplicate
            id="duplicate-process"
            type="process"
            publicId={process.publicId}
            open={duplicateModal}
            setOpen={setDuplicateModal}
            isLockedTemplate={process && process.lockedTemplate}
          />
        )}

        {authModal && (
          <AuthModal
            id="auth-modal"
            open={authModal}
            setOpen={setAuthModal}
            duplicateDocumentId={process.publicId}
            text="Sign up for free to copy this doc and make it your own"
          />
        )}
        {permissionsModal && (
          <PermissionsModal
            id="permissions-modal"
            open={permissionsModal}
            setOpen={setPermissionsModal}
            onClose={() => setSettingsMenu(INITIAL_CONTEXT_MENU_STATE)}
          />
        )}
        {defaultVariablesModal && (
          <DefaultVariablesModal
            id="default-variables-modal"
            open={defaultVariablesModal}
            setOpen={setDefaultVariablesModal}
            onClose={() => setSettingsMenu(INITIAL_CONTEXT_MENU_STATE)}
          />
        )}
        {duplicateChildrenModal && (
          <DuplicateChildren
            id="duplicate-children-modal"
            open={duplicateChildrenModal}
            setOpen={setDuplicateChildrenModa}
            publicId={process.publicId}
            type="process"
          />
        )}
      </PageHeader>
    )
  } else {
    return <div />
  }
}

export default Toolbar
